import { MailCheck } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';

import { useRequestEmailChange } from '../hooks/use-account-mutations';
import { emailChangeSchema, type EmailChangeValues } from '../schemas/account-schemas';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['newEmail', 'currentPassword']);
const DEFAULTS: EmailChangeValues = { newEmail: '', currentPassword: '' };

/** The sign-in email changes only after the link sent to the new address is opened. */
export function EmailCard({ email }: { email: string }) {
  const { t } = useTranslation(['account', 'common']);
  const message = useErrorMessage();
  const request = useRequestEmailChange();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const form = useForm<EmailChangeValues>({
    resolver: useI18nResolver(emailChangeSchema),
    defaultValues: DEFAULTS,
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await request.mutateAsync(values);
      setSentTo(values.newEmail);
      form.reset(DEFAULTS);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <FormSection
      id={SECTION_ANCHORS.signInEmail}
      tour="account.email"
      title={t('account:email.title')}
      description={t('account:email.description', { email })}
    >
      {sentTo ? (
        <Alert role="status">
          <MailCheck aria-hidden />
          <AlertTitle>{t('account:email.sentTitle')}</AlertTitle>
          <AlertDescription>{t('account:email.sent', { email: sentTo })}</AlertDescription>
        </Alert>
      ) : null}
      <Form {...form}>
        <form
          onSubmit={submit}
          noValidate
          className="flex flex-col gap-4"
          aria-label={t('account:email.title')}
        >
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="newEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('account:email.newEmail')}</FormLabel>
                  <FormControl>
                    <Input type="email" autoComplete="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="currentPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('account:email.currentPassword')}</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="current-password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={request.isPending}>
              {request.isPending ? t('account:email.sending') : t('account:email.submit')}
            </Button>
          </div>
        </form>
      </Form>
    </FormSection>
  );
}
